import type { DeviceInfo } from 'ngx-device-detector';
import { type RetryConfig, timer } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import type * as fromGenerated from '../_generated';

/** Options for exponential backoff retry. */
export interface ExponentialBackoffRetryOptions {
  /** Max number of retries (total attempts = count + 1). */
  count: number;
  /** Initial delay in ms; doubles each retry (1st retry: baseDelayMs, 2nd: 2×, 3rd: 4×, …). */
  baseDelayMs: number;
  /** Cap delay in ms (no delay will exceed this). */
  capDelayMs: number;
}

/**
 * Builds a retry config for RxJS `retry()` with exponential backoff.
 * Delay for retry N: min(baseDelayMs * 2^(N-1), capDelayMs).
 */
export function createExponentialBackoffRetryConfig(
  options: ExponentialBackoffRetryOptions,
): RetryConfig {
  const { count, baseDelayMs, capDelayMs } = options;
  return {
    count,
    delay: (_error: unknown, retryCount: number) => {
      const delayMs = Math.min(
        baseDelayMs * Math.pow(2, retryCount - 1),
        capDelayMs,
      );
      return timer(delayMs);
    },
  };
}

const SESSION_ID_KEY = 'squadbox_heartbeat_session_id';

/**
 * Creates session information consisting of a unique request ID and a session ID.
 * If a session ID does not already exist in the session storage, it generates one and stores it.
 * A new request ID is generated every time the method is called.
 *
 * @return An object containing the generated request ID and session ID.
 */
export const createSessionInfo = (): {
  requestId: string;
  sessionId: string;
} => {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = uuidv4();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }

  return { requestId: uuidv4(), sessionId };
};

/**
 * Creates and returns context information detailing the application's environment,
 * device information, locale, and page data.
 *
 * @return {fromGenerated.AnalyticsDto['context']} The context information including environment, app details, device specifications, locale, and the current page URL.
 */
export const createContextInfo = ({
  env,
  version,
  deviceInfo,
  currentLang,
}: {
  env: string;
  version: string;
  deviceInfo: DeviceInfo;
  currentLang: string;
}): fromGenerated.AnalyticsDto['context'] => {
  return {
    env,
    app: {
      name: 'squadbox',
      version,
    },
    device: {
      os: deviceInfo.os,
      browser: deviceInfo.browser,
      browserVersion: deviceInfo.browser_version,
    },
    locale: currentLang,
    page: {
      url: globalThis.location.href,
    },
  };
};

/**
 * Determines if the chat is starting a new conversation by counting user messages instead of total messages.
 *
 * @param {fromGenerated.ChatWithMessagesView} chatWithMessages - The chat view object containing a list of messages.
 * @return {boolean} `true` if the chat is starting a new conversation, `false` otherwise.
 */
export const isStartingNewConversation = (
  chatWithMessages: Pick<fromGenerated.ChatWithMessagesView, 'messages'>,
): boolean => {
  const userMessageCount = chatWithMessages.messages.filter(
    (message) => message.isFromUser,
  ).length;

  return userMessageCount === 1; // If there's exactly 1 user message, it's a new conversation
};
